import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/lib/api.ts'
import { useModuleStatus } from '@/hooks/useModuleStatus.ts'
import {
  FileCode, Search, Upload, Download, Lock,
  File, FileText, Image, Building2, Eye,
  User, Copy, Link as LinkIcon, Shield,
  HardDrive, Monitor,
} from 'lucide-react'
import {
  Badge,
  BetaBadge,
  Button,
  Card,
  DataTable,
  EmptyState,
  IconButton,
  Input,
  PageHeader,
  Select,
  StatusBadge,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Tooltip,
  dateColumn,
  type ColumnDef,
} from '@/components/ui'

interface Document {
  id: string
  filename: string
  mimeType: string
  sizeBytes: number
  isEncrypted: boolean
  customerName?: string
  deviceName?: string
  version: number
  createdByName: string
  createdAt: string
}

interface PeripheryCopy {
  id: string
  sourceName: string
  sourceCustomer: string
  targetCustomer: string
  devicesCount: number
  status: 'pending' | 'completed' | 'failed'
  createdAt: string
}

type SubPage = 'documents' | 'periphery'

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`
}

function getFileIcon(mimeType: string) {
  if (mimeType.startsWith('image/')) return Image
  if (mimeType.includes('pdf')) return FileText
  return File
}

export function DokumentationPage() {
  const { isBeta } = useModuleStatus('dokumentation')
  const [subPage, setSubPage] = useState<SubPage>('documents')

  return (
    <div className="space-y-4">
      <PageHeader
        title={
          <span className="flex items-center gap-2">
            <FileCode className="w-7 h-7 text-brand-500" />
            Dokumentation &amp; Peripherie
            {isBeta ? <BetaBadge size="md" /> : null}
          </span>
        }
        description="IT-Dokumentation, verschlüsselte PDFs und Gerätekonfigurationen"
      />

      <Tabs value={subPage} onValueChange={(v) => setSubPage(v as SubPage)}>
        <TabsList>
          <TabsTrigger value="documents">
            <FileText className="w-4 h-4" />
            Dokumente
          </TabsTrigger>
          <TabsTrigger value="periphery">
            <Copy className="w-4 h-4" />
            Peripherie kopieren
          </TabsTrigger>
        </TabsList>

        <TabsContent value="documents">
          <DocumentsSection />
        </TabsContent>

        <TabsContent value="periphery">
          <PeripherySection />
        </TabsContent>
      </Tabs>
    </div>
  )
}

function DocumentsSection() {
  const [search, setSearch] = useState('')

  const { data: documents = [] } = useQuery<Document[]>({
    queryKey: ['documents', search],
    queryFn: () => apiClient.get('/documents', { params: { q: search || undefined } }).then((r) => r.data),
    retry: false,
  })

  const columns: ColumnDef<Document, unknown>[] = useMemo(
    () => [
      {
        id: 'filename',
        header: 'Dokument',
        cell: ({ row }) => {
          const doc = row.original
          const FileIcon = getFileIcon(doc.mimeType)
          return (
            <div className="flex items-center gap-3 min-w-0">
              <FileIcon className="w-7 h-7 text-gray-400 flex-shrink-0" />
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-small font-medium text-gray-900 truncate">
                    {doc.filename}
                  </span>
                  {doc.isEncrypted ? (
                    <Tooltip content="Verschlüsselt">
                      <Lock className="w-3.5 h-3.5 text-amber-500" />
                    </Tooltip>
                  ) : null}
                  <Badge variant="neutral" size="sm">v{doc.version}</Badge>
                </div>
                <div className="flex items-center gap-3 text-caption text-gray-500 mt-0.5">
                  {doc.customerName ? (
                    <span className="flex items-center gap-0.5">
                      <Building2 className="w-3 h-3" />
                      {doc.customerName}
                    </span>
                  ) : null}
                  {doc.deviceName ? (
                    <span className="flex items-center gap-0.5">
                      <Monitor className="w-3 h-3" />
                      {doc.deviceName}
                    </span>
                  ) : null}
                  <span className="tabular-nums">{formatBytes(doc.sizeBytes)}</span>
                  <span className="flex items-center gap-0.5">
                    <User className="w-3 h-3" />
                    {doc.createdByName}
                  </span>
                </div>
              </div>
            </div>
          )
        },
      },
      dateColumn<Document>('createdAt', 'Erstellt', { size: 120 }),
      {
        id: '__actions__',
        header: '',
        enableSorting: false,
        size: 140,
        cell: () => (
          <div className="flex items-center justify-end gap-1">
            <Tooltip content="Herunterladen">
              <IconButton
                size="sm"
                variant="ghost"
                icon={<Download />}
                aria-label="Herunterladen"
              />
            </Tooltip>
            <Tooltip content="Sicheren Link erstellen">
              <IconButton
                size="sm"
                variant="ghost"
                icon={<LinkIcon />}
                aria-label="Sicheren Link erstellen"
              />
            </Tooltip>
            <Tooltip content="Ansehen">
              <IconButton
                size="sm"
                variant="ghost"
                icon={<Eye />}
                aria-label="Ansehen"
              />
            </Tooltip>
          </div>
        ),
      },
    ],
    [],
  )

  return (
    <div className="space-y-4">
      <div className="flex gap-2 items-center">
        <Input
          wrapperClassName="flex-1"
          leadingIcon={<Search />}
          placeholder="Dokument, Kunde oder Gerät suchen …"
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
        />
        <Button leadingIcon={<Upload />}>Hochladen</Button>
      </div>

      <DataTable<Document>
        data={documents}
        columns={columns}
        getRowId={(d) => d.id}
        emptyState={
          <EmptyState
            title="Keine Dokumente gefunden"
            description="Lade ein neues Dokument hoch oder ändere deinen Suchbegriff."
            icon={<FileText />}
          />
        }
      />

      <Card>
        <Card.Body>
          <div className="flex items-start gap-3">
            <span className="shrink-0 mt-0.5 text-brand-500 [&>svg]:h-4 [&>svg]:w-4">
              <Shield />
            </span>
            <div className="flex flex-col gap-1">
              <div className="text-small font-semibold text-gray-900">
                Sicherer Dokumentenversand
              </div>
              <p className="text-caption text-gray-600">
                Dokumente können als verschlüsselte PDFs (AES-256) über Einmal-Links versendet werden.
                Der Link verfällt nach maximal 7 Tagen oder nach dem ersten Abruf.
              </p>
            </div>
          </div>
        </Card.Body>
      </Card>
    </div>
  )
}

function PeripherySection() {
  const { data: copies = [] } = useQuery<PeripheryCopy[]>({
    queryKey: ['periphery-copies'],
    queryFn: () => apiClient.get('/documents/periphery').then((r) => r.data),
    retry: false,
  })

  const [sourceDevice, setSourceDevice] = useState<string | undefined>()
  const [targetCustomer, setTargetCustomer] = useState<string | undefined>()

  return (
    <div className="space-y-4">
      <Card>
        <Card.Header>
          <div className="flex items-center gap-2">
            <HardDrive className="w-4 h-4 text-brand-500" />
            <Card.Title>Smart-Copy: Gerätekonfiguration kopieren</Card.Title>
          </div>
        </Card.Header>
        <Card.Body className="space-y-4">
          <p className="text-caption text-gray-500">
            Wähle ein Geräteprofil aus CWA oder TANSS und kopiere die Konfiguration auf
            einen oder mehrere neue Geräte.
          </p>
          <div className="grid grid-cols-2 gap-4">
            <Select
              label="Quell-Gerät / Profil"
              placeholder="— Gerät auswählen —"
              value={sourceDevice}
              onValueChange={setSourceDevice}
              options={[
                { value: 'template-standard', label: 'Template: Standard-Arbeitsplatz' },
                { value: 'template-server', label: 'Template: Server-Konfiguration' },
                { value: 'srv-dc01', label: 'SRV-DC01 (Müller IT GmbH)' },
                { value: 'ws-rec01', label: 'WS-REC01 (Fischer AG)' },
              ]}
            />
            <Select
              label="Ziel-Kunde"
              placeholder="— Kunde auswählen —"
              value={targetCustomer}
              onValueChange={setTargetCustomer}
              options={[
                { value: 'mueller', label: 'Müller IT GmbH' },
                { value: 'schmidt', label: 'Schmidt & Partner' },
                { value: 'fischer', label: 'Fischer AG' },
              ]}
            />
          </div>
          <div>
            <Button leadingIcon={<Copy />}>Konfiguration kopieren</Button>
          </div>
        </Card.Body>
      </Card>

      <Card>
        <Card.Header>
          <Card.Title>Letzte Kopier-Vorgänge</Card.Title>
        </Card.Header>
        <Card.Body className="p-0">
          {copies.length === 0 ? (
            <EmptyState
              title="Noch keine Kopier-Vorgänge"
              description="Starte eine Gerätekonfiguration oben."
              icon={<Copy />}
              size="sm"
            />
          ) : (
            <DataTable
              data={copies}
              getRowId={(c) => c.id}
              enableSorting={false}
              columns={[
                {
                  id: 'source-target',
                  header: 'Quelle → Ziel',
                  cell: ({ row }) => (
                    <span className="font-medium text-gray-900">
                      {row.original.sourceName} → {row.original.targetCustomer}
                    </span>
                  ),
                },
                {
                  accessorKey: 'devicesCount',
                  header: 'Geräte',
                  size: 80,
                  cell: ({ row }) => (
                    <span className="block text-right tabular-nums text-gray-600">{row.original.devicesCount}</span>
                  ),
                },
                {
                  accessorKey: 'createdAt',
                  header: 'Erstellt',
                  size: 130,
                  cell: ({ row }) => (
                    <span className="text-gray-600">{new Date(row.original.createdAt).toLocaleDateString('de-DE')}</span>
                  ),
                },
                {
                  accessorKey: 'status',
                  header: 'Status',
                  size: 150,
                  cell: ({ row }) => <StatusBadge status={row.original.status} />,
                },
              ]}
            />
          )}
        </Card.Body>
      </Card>
    </div>
  )
}
